import { BillingDoc, BillingDocLineItem, BillingDocWithLineItems } from '@atlassian/cssxp-types-billing';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { Organization } from '@atlassian/cssxp-types-organization';
import {
  ATLASSIAN_ACCESS_PATH,
  BITBUCKET_PATH,
  CLOUD_SITE_PATH,
  GENERIC_ENTITLEMENT_PATH,
  NOT_FOUND_PATH,
  SERVER_LICENSE_PATH,
  SERVICE_LICENSE_PATH,
  SourceSystem,
  STATUSPAGE_PATH,
  CCP_ENTITLEMENT_PATH,
} from '@atlassian/cssxp-typescommon';
import { UnclaimedSiteLabel } from '@atlassian/cssxp-utils-entitlement';
import {
  isAtlassianAccess,
  isBitbucketCloud,
  isCloudParent,
  isOrganisationParent,
  isStatuspage,
  isJiraProduct,
  isNpfProduct,
  SERVICE_BASE_PRODUCTS,
  isConfluenceProduct,
} from '@atlassian/cssxp-utils-product';
import * as React from 'react';
import { BreadcrumbItem } from '../../breadcrumbs/src/breadcrumbs';

const getHeaderDefault = (billingDoc: BillingDoc): React.ReactNode => billingDoc.id;

const getSubheaderDefault = (_billingDoc: BillingDoc): React.ReactNode => '';

const getIconDefault = (_billingDoc: BillingDoc): JSX.Element => <div />;

const getActionsDefault = (
  _billingDoc: BillingDoc,
  _onComplete: () => Promise<ApolloQueryResult<unknown>>,
  _adabaseurl: string
): React.ReactNode => undefined;

const getActionsDefaultQuote = (_billingDoc: BillingDoc, _adabaseurl?: string): React.ReactNode => undefined;

const getEntitlementRouteFromLineItem = (lineItem: BillingDocLineItem) => {
  const {
    cloudSiteId,
    platform,
    productId,
    productKey,
    baseProduct,
    businessKey,
    sen,
    parentEntitlementKey,
    entitlementNumber,
    entitlement,
  } = lineItem;

  // Converge Billing Docs to similar shape
  const product = productId ?? productKey ?? entitlement?.productId ?? '';
  const id = businessKey ?? sen ?? entitlement?.id ?? entitlementNumber;
  if (!product) {
    return NOT_FOUND_PATH;
  }

  const parentKey = entitlement?.parentSen ?? parentEntitlementKey;

  // Copied from getEntitlementRoute, eventually converge
  if (SERVICE_BASE_PRODUCTS.includes(`${baseProduct}`)) {
    return `${SERVICE_LICENSE_PATH}/${id}`;
  } else if (`${platform}`.toUpperCase() !== 'CLOUD') {
    return `${SERVER_LICENSE_PATH}/${id}`;
  } else if (isOrganisationParent(product) || isAtlassianAccess(product)) {
    return `${ATLASSIAN_ACCESS_PATH}/${cloudSiteId}`;
  } else if (isBitbucketCloud(product)) {
    return `${BITBUCKET_PATH}/${cloudSiteId ?? id}`;
  } else if (isStatuspage(product)) {
    return `${STATUSPAGE_PATH}/${parentKey ?? entitlementNumber ?? id}`;
  } else if (isCloudParent(product) || parentKey) {
    return `${CLOUD_SITE_PATH}/${cloudSiteId ?? id}`;
  } else if (isNpfProduct(product) || isJiraProduct(product) || isConfluenceProduct(product)) {
    return `${CCP_ENTITLEMENT_PATH}/${entitlementNumber ?? id}`;
  } else {
    return `${CCP_ENTITLEMENT_PATH}/${id}`;
  }
};

const getEntitlementNameFromLineItem = (lineItem: BillingDocLineItem, sourceSystem?: SourceSystem) => {
  // New quotes do not have an associated entitlement / SEN key
  if (!lineItem.businessKey && !lineItem.sen) {
    return undefined;
  }

  if (sourceSystem === SourceSystem.CCP) {
    return `${lineItem.cloudSiteHostname ? `${lineItem.cloudSiteHostname}, ` : ''}${
      lineItem.entitlementNumber ?? lineItem.entitlementId
    }`;
  }
  if (`${lineItem.platform}`.toUpperCase() === 'CLOUD') {
    const cloudSiteHostname = lineItem.cloudSiteHostname ?? UnclaimedSiteLabel;

    return `${cloudSiteHostname}, SEN-${lineItem.businessKey ?? lineItem.sen}`;
  }
  return `SEN-${lineItem.businessKey}`;
};

const getBreadcrumbTrailDefault = (
  _billingDoc: BillingDocWithLineItems,
  _organization?: Organization
): BreadcrumbItem[] => [];

export {
  getActionsDefault,
  getIconDefault,
  getHeaderDefault,
  getSubheaderDefault,
  getBreadcrumbTrailDefault,
  getEntitlementRouteFromLineItem,
  getEntitlementNameFromLineItem,
  getActionsDefaultQuote,
};
